import { Helmet } from 'react-helmet-async';
import { Box, Container, Grid, Typography, Avatar, Card, CardContent, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  School as SchoolIcon,
  Lightbulb as LightbulbIcon,
  Groups as GroupsIcon,
  EmojiEvents as EmojiEventsIcon
} from '@mui/icons-material';
import { LandingLayout } from '../components/landing/LandingLayout';
import { Footer } from '../components/landing/Footer';

const values = [
  {
    title: 'Innovation in Learning',
    description: 'We believe in leveraging cutting-edge technology to create more effective and engaging learning experiences.',
    icon: <LightbulbIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: 'Accessible Education',
    description: 'Making quality education accessible to everyone, regardless of their background or circumstances.',
    icon: <SchoolIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: 'Community First',
    description: 'Building a supportive learning community where students can grow and succeed together.',
    icon: <GroupsIcon sx={{ fontSize: 40 }} />,
  },
  {
    title: 'Excellence',
    description: 'Committed to delivering the highest quality learning tools and experiences.',
    icon: <EmojiEventsIcon sx={{ fontSize: 40 }} />,
  }
];

export const AboutPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const clayCard = {
    borderRadius: '20px',
    border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.primary.main, 0.12)}`,
    boxShadow: isDark
      ? [
          '0 16px 40px rgba(0,0,0,0.45)',
          '0 6px 12px rgba(0,0,0,0.28)',
          'inset 0 2px 0 rgba(255,255,255,0.06)',
          'inset 0 -2px 0 rgba(0,0,0,0.2)',
        ].join(', ')
      : [
          '0 16px 40px rgba(99,102,241,0.14)',
          '0 6px 12px rgba(0,0,0,0.07)',
          'inset 0 2px 0 rgba(255,255,255,0.95)',
          'inset 0 -2px 0 rgba(99,102,241,0.07)',
        ].join(', '),
  };

  const clayCardHover = {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: isDark
      ? [
          '0 24px 56px rgba(0,0,0,0.55)',
          '0 10px 20px rgba(0,0,0,0.32)',
          'inset 0 2px 0 rgba(255,255,255,0.07)',
          'inset 0 -2px 0 rgba(0,0,0,0.22)',
        ].join(', ')
      : [
          '0 24px 56px rgba(99,102,241,0.2)',
          '0 10px 20px rgba(0,0,0,0.09)',
          'inset 0 2px 0 rgba(255,255,255,1)',
          'inset 0 -2px 0 rgba(99,102,241,0.1)',
        ].join(', '),
  };

  return (
    <LandingLayout>
      <Helmet>
        <title>About BrainLoop - Our Mission to Transform Learning</title>
        <meta name="description" content="Learn about BrainLoop's mission to make learning more effective and accessible with AI-powered study loops, spaced repetition, and personalized flashcard generation." />
        <link rel="canonical" href="https://brainloop.app/about" />
        <meta property="og:title" content="About BrainLoop - Our Mission to Transform Learning" />
        <meta property="og:description" content="Learn about BrainLoop's mission to make learning more effective and accessible with AI-powered study loops and spaced repetition." />
        <meta property="og:url" content="https://brainloop.app/about" />
      </Helmet>

      <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>

        {/* Subtle background blobs for depth */}
        <Box sx={{
          position: 'absolute', top: -80, left: '10%',
          width: 500, height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.094)}, transparent 65%)`,
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: 100, right: '5%',
          width: 400, height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.071)}, transparent 65%)`,
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>

          {/* Hero */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            {/* Accent pill */}
            <Box
              sx={{
                display: 'inline-block',
                mb: 3,
                px: 2.5,
                py: 0.75,
                borderRadius: '20px',
                background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                color: 'primary.contrastText',
                fontSize: 'caption.fontSize',
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                boxShadow: [
                  '0 6px 16px rgba(99,102,241,0.45)',
                  '0 2px 6px rgba(0,0,0,0.1)',
                  'inset 0 1.5px 0 rgba(255,255,255,0.3)',
                  'inset 0 -1.5px 0 rgba(0,0,0,0.14)',
                ].join(', '),
              }}
            >
              Our Story
            </Box>

            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Building the Future of Learning
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto', lineHeight: 1.6 }}>
              Transforming education through technology and innovation
            </Typography>
          </Box>

          {/* Mission card — the big clay statement */}
          <Card
            elevation={0}
            sx={{
              mb: 8,
              bgcolor: isDark ? alpha(theme.palette.primary.main, 0.08) : alpha(theme.palette.primary.main, 0.04),
              ...clayCard,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              boxShadow: isDark
                ? [
                    '0 20px 50px rgba(0,0,0,0.5)',
                    '0 8px 16px rgba(99,102,241,0.15)',
                    'inset 0 3px 0 rgba(255,255,255,0.06)',
                    'inset 0 -3px 0 rgba(0,0,0,0.22)',
                  ].join(', ')
                : [
                    '0 20px 50px rgba(99,102,241,0.18)',
                    '0 8px 16px rgba(0,0,0,0.07)',
                    'inset 0 3px 0 rgba(255,255,255,0.95)',
                    'inset 0 -3px 0 rgba(99,102,241,0.09)',
                  ].join(', '),
            }}
          >
            <CardContent sx={{ p: { xs: 3.5, md: 5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                {/* Clay icon accent */}
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'grid',
                    placeItems: 'center',
                    background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                        boxShadow: [
                      '0 8px 20px rgba(99,102,241,0.45)',
                      '0 3px 6px rgba(0,0,0,0.15)',
                      'inset 0 2px 0 rgba(255,255,255,0.3)',
                      'inset 0 -2px 0 rgba(0,0,0,0.15)',
                    ].join(', '),
                    color: 'primary.contrastText',
                  }}
                >
                  <SchoolIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Our Mission
                </Typography>
              </Box>
              <Typography
                variant="body1"
                color="text.secondary"
                  sx={{ lineHeight: 1.85, maxWidth: 780 }}
              >
                At BrainLoop, we're on a mission to revolutionize the way people learn. We believe that education should be personalized, engaging, and accessible to everyone. Our platform combines cutting-edge AI technology with proven learning methodologies to create an experience that adapts to each learner's unique needs and preferences.
              </Typography>
            </CardContent>
          </Card>

          {/* Values section */}
          <Box sx={{ mb: 8 }}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              {/* Accent pill */}
              <Box
                sx={{
                  display: 'inline-block',
                  mb: 2,
                  px: 2,
                  py: 0.6,
                  borderRadius: '20px',
                  background: `linear-gradient(145deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
                  color: 'primary.contrastText',
                  fontSize: 'caption.fontSize',
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  boxShadow: [
                    '0 5px 14px rgba(99,102,241,0.4)',
                    'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                    'inset 0 -1.5px 0 rgba(0,0,0,0.12)',
                  ].join(', '),
                }}
              >
                What We Stand For
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                Our Values
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {values.map((value, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'background.paper',
                      ...clayCard,
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                      '&:hover': clayCardHover,
                      '&:hover .value-avatar': {
                        boxShadow: [
                          '0 12px 30px rgba(99,102,241,0.6)',
                          '0 4px 10px rgba(0,0,0,0.2)',
                          'inset 0 2.5px 0 rgba(255,255,255,0.35)',
                          'inset 0 -2.5px 0 rgba(0,0,0,0.2)',
                        ].join(', '),
                        transform: 'scale(1.08)',
                      },
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3.5 }}>
                      <Avatar
                        className="value-avatar"
                        sx={{
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 2.5,
                          bgcolor: 'transparent',
                          background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
                          boxShadow: [
                            '0 10px 24px rgba(99,102,241,0.5)',
                            '0 4px 8px rgba(0,0,0,0.18)',
                            'inset 0 2.5px 0 rgba(255,255,255,0.32)',
                            'inset 0 -2.5px 0 rgba(0,0,0,0.18)',
                          ].join(', '),
                          transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                        }}
                      >
                        {value.icon}
                      </Avatar>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        {value.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

        </Container>
      </Box>
      <Footer />
    </LandingLayout>
  );
};
